import {
    ApiError,
    REFRESH,
    clearSignedInHint,
    v2,
} from '../../screens/account/api';

/**
 * Lists (prototype UserLists / ListDetail / SaveToListModal) against api-next's
 * `lists` module at /v2. Items are live restaurants or dishes by their legacy
 * id; the pages read the dishes themselves from the main API.
 */

export type ListType = 'RESTAURANTS' | 'MEALS';

export interface ListItem {
    id: string;
    legacyRestaurantId: string | null;
    legacyMealId: string | null;
    addedAt: string;
}

export interface UserList {
    id: string;
    name: string;
    slug: string;
    type: ListType;
    isPublic: boolean;
    itemCount: number;
    isMine: boolean;
    owner: { username: string; displayName: string | null };
    items: ListItem[];
}

/** api-next's limit (resolvers/lists LIST_LIMITS.name). */
export const MAX_LIST_NAME = 100;

const LIST = `id name slug type isPublic itemCount isMine owner { username displayName }
    items { id legacyRestaurantId legacyMealId addedAt }`;

export const MY_LISTS = `query MyLists { myLists { ${LIST} } }`;
export const USER_LISTS = `query UserLists($username: String!) { userLists(username: $username) { ${LIST} } }`;
export const USER_LIST = `query UserList($username: String!, $slug: String!) {
    userList(username: $username, slug: $slug) { ${LIST} }
}`;
export const ITEM_SAVED_LIST_IDS = `query ItemSavedListIds($legacyRestaurantId: ID, $legacyMealId: ID) {
    itemSavedListIds(legacyRestaurantId: $legacyRestaurantId, legacyMealId: $legacyMealId)
}`;
export const CREATE_LIST = `mutation CreateUserList($input: CreateListInput!) { createUserList(input: $input) { ${LIST} } }`;
export const UPDATE_LIST = `mutation UpdateUserList($id: ID!, $input: UpdateListInput!) {
    updateUserList(id: $id, input: $input) { ${LIST} }
}`;
export const DELETE_LIST = `mutation DeleteUserList($id: ID!) { deleteUserList(id: $id) }`;
export const ADD_TO_LIST = `mutation AddToList($listId: ID!, $legacyRestaurantId: ID, $legacyMealId: ID) {
    addToList(listId: $listId, legacyRestaurantId: $legacyRestaurantId, legacyMealId: $legacyMealId) { id itemCount }
}`;
export const REMOVE_FROM_LIST = `mutation RemoveFromList($listId: ID!, $legacyRestaurantId: ID, $legacyMealId: ID) {
    removeFromList(listId: $listId, legacyRestaurantId: $legacyRestaurantId, legacyMealId: $legacyMealId) { id itemCount }
}`;

/** Thrown when the session has gone for good (the caller asks to sign in). */
export class SignedOutError extends Error {}

/**
 * v2() with the one renewal HeaderAccount and the correction dialog do: access
 * tokens last an hour, so an UNAUTHENTICATED answer is retried once after
 * refreshing with the refresh cookie. Signed-in-only calls only.
 */
export async function v2Session<T>(
    query: string,
    variables: Record<string, unknown> = {},
): Promise<T> {
    try {
        return await v2<T>(query, variables);
    } catch (err) {
        if (!(err instanceof ApiError && err.code === 'UNAUTHENTICATED')) {
            throw err;
        }
    }
    try {
        await v2(REFRESH);
    } catch {
        clearSignedInHint();
        throw new SignedOutError();
    }
    try {
        return await v2<T>(query, variables);
    } catch (err) {
        if (err instanceof ApiError && err.code === 'UNAUTHENTICATED') {
            clearSignedInHint();
            throw new SignedOutError();
        }
        throw err;
    }
}

/** What saving changes, as the prototype's Confirm does: lists to add to and to leave. */
export function listChanges(
    initial: ReadonlySet<string>,
    selected: ReadonlySet<string>,
) {
    return {
        add: [...selected].filter(id => !initial.has(id)),
        remove: [...initial].filter(id => !selected.has(id)),
    };
}

/** The prototype's toast after Confirm, or null when nothing changed. */
export function savedMessage(change: {
    add: string[];
    remove: string[];
}): string | null {
    if (change.add.length && change.remove.length) return 'Lists updated';
    if (change.add.length) return 'Saved to list';
    if (change.remove.length) return 'Removed from list';
    return null;
}

export const itemCountText = (n: number) =>
    `${n} ${n === 1 ? 'item' : 'items'}`;

export const listPath = (username: string, slug: string) =>
    `/user/${encodeURIComponent(username)}/list/${encodeURIComponent(slug)}`;
export const listsPath = (username: string) =>
    `/user/${encodeURIComponent(username)}/lists`;

export const ownerName = (owner: UserList['owner']) =>
    owner.displayName?.trim() || owner.username;

/** Variables naming the item for addToList / removeFromList / itemSavedListIds. */
export const itemVariables = (item: {
    legacyRestaurantId?: string;
    legacyMealId?: string;
}) =>
    item.legacyMealId
        ? { legacyMealId: item.legacyMealId }
        : { legacyRestaurantId: item.legacyRestaurantId };

export function errorText(err: unknown, fallback: string): string {
    if (err instanceof ApiError) {
        if (err.code === 'FEATURE_DISABLED') {
            return "Lists aren't available right now. Please try again later.";
        }
        if (
            err.code === 'BAD_USER_INPUT' ||
            err.code === 'BUSINESS_RULE' ||
            err.code === 'NOT_FOUND'
        ) {
            return err.message;
        }
    }
    return fallback;
}

/** Of these, the ids on any of the caller's lists (search results' Saved marks). */
export const SAVED_ITEM_IDS = `query SavedItemIds($legacyMealIds: [ID!], $legacyRestaurantIds: [ID!]) {
    savedItemIds(legacyMealIds: $legacyMealIds, legacyRestaurantIds: $legacyRestaurantIds)
}`;
